import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FieldErrors, useForm } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import {
  PropertyAdditionalInfoForm,
  PropertyUpdateDataApi,
  PublishCatalogs,
} from '../models/propertyModel';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import {
  getPropertyTagsEndpoint,
  getPropertyToEditEndpoint,
  getTypePropertiesEndpoint,
  updatePropertyEndpoint,
} from '../services/propertyService';
import useAuth from '../../../hooks/useAuth';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { getCurrenciesPerCountry } from '../../../services/publicService';
import useUserCountry from '../../../hooks/useUserCountry';
import {
  listToSelectOptionAdapter,
  plainListToSelectOptionAdapter,
} from '../../../adapters/listAdapter';
import { Option } from '../../../models/formModel';
import useProperList from '../../../hooks/useProperList';
import { useToast } from '../../../components/toast/ToastProvider';

interface Props {
  id: string;
}

const useAdminAdditionalInfo = ({ id }: Props) => {
  const toast = useToast();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const [formUpdateData, setFormUpdateData] = useState<PropertyUpdateDataApi>();
  const [loadingCurrency, setLoadingCurrency] = useState<boolean>(true);
  const { getCurrentCountry } = useUserCountry();
  const currentUserCountry = getCurrentCountry()?.code || '';
  const [currentCurrency, setCurrentCurrency] = useState<Option>({
    label: 'Selecciona una moneda',
    value: '',
  });
  const [catalogs, setCatalogs] = useState<PublishCatalogs>({
    countries: [],
    characteristics: [],
    amenities: [],
    propertyTypes: [],
    plans: [],
    currencies: [],
    tags: [],
  });

  const { propersOptions, isLoadingPropers } = useProperList();

  // Ids de tipos "terreno": no llevan número de casa o apartamento.
  const landTypeIds = useRef<string[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    reset,
    watch,
  } = useForm<PropertyAdditionalInfoForm>({
    resolver: yupResolver(
      yup
        .object({
          type_id: yup
            .string()
            .nullable()
            .required('Tipo de propiedad requerida'),
          // el título público lo arma el backend con tipo + ubicación; `name` ya no se pide
          name: yup.string().optional(),
          description: yup.string().required('Descripción es requerida'),
          property_number: yup
            .string()
            .test(
              'property-number-required',
              'Número de propiedad requerido',
              function (value) {
                return (
                  landTypeIds.current.includes(this.parent.type_id) || !!value
                );
              },
            ),
          currency: yup.string().required('Debes seleccionar moneda'),
          landlord_id: yup.string(),
          proper_id: yup.string().required('Proper es requerido'),
          has_propi_property_keys: yup.boolean(),
          location_property_keys: yup.string(),
          property_tags: yup.array().of(yup.string()),
        })
        .required(),
    ),
  });

  // Load property to update and catalogs
  useEffect(() => {
    const callCatalogs = async (updateData: PropertyUpdateDataApi) => {
      const propertyTypes = await callEndpoint(
        getTypePropertiesEndpoint(token),
      );
      if (propertyTypes.status !== 200) {
        alert('Ocurrio un error al momento de cargar los catalogos de tipos');
        return;
      }
      const currencies = await callEndpoint(
        getCurrenciesPerCountry(currentUserCountry),
      );
      const tags = await callEndpoint(getPropertyTagsEndpoint(token));
      const propertyTypesResponse = fetchResponseAdapter(propertyTypes.data);
      const currenciesResponse = fetchResponseAdapter(currencies.data);
      const tagsResponse = fetchResponseAdapter(tags.data);
      landTypeIds.current = (propertyTypesResponse.data || [])
        .filter((item: any) => /terreno|lote|land/i.test(item?.type ?? ''))
        .map((item: any) => item.id);
      setCatalogs({
        propertyTypes: propertyTypesResponse.data,
        currencies: plainListToSelectOptionAdapter(currenciesResponse.data),
        tags: listToSelectOptionAdapter(tagsResponse.data || [], 'id', 'name'),
      });
      setCurrentCurrency({
        label: updateData.currency ? updateData.currency : '',
        value: updateData.currency ? updateData.currency : '',
      });
      setLoadingCurrency(false);
    };

    const loadExistingProperty = async () => {
      const { status, data } = await callEndpoint(
        getPropertyToEditEndpoint(id, token),
      );
      if (status === 200) {
        const { data: formData } = fetchResponseAdapter(data);
        setFormUpdateData(formData);
        return formData;
      }
    };

    loadExistingProperty()
      .then((updateData) => callCatalogs(updateData))
      .catch((e) => console.error(e));
  }, []);

  // PRE load update data to form
  useEffect(() => {
    if (formUpdateData) {
      reset({
        type_id: formUpdateData.type?.id ?? '',
        name: formUpdateData.name ?? '',
        description: formUpdateData.description ?? '',
        property_number: formUpdateData.property_number ?? '',
        has_propi_property_keys:
          formUpdateData.has_propi_property_keys || false,
        // "NA" es un valor de relleno, no una instrucción real.
        location_property_keys:
          !formUpdateData.location_property_keys ||
          formUpdateData.location_property_keys === 'NA'
            ? ''
            : formUpdateData.location_property_keys,
        currency: formUpdateData.currency,
        proper_id: formUpdateData.proper_id,
        property_tags: formUpdateData.property_tags || [],
      });
    }
  }, [formUpdateData]);

  const doAction = async (form: PropertyAdditionalInfoForm) => {
    const { status } = await callEndpoint(
      updatePropertyEndpoint(form, id, token),
    );
    if (status === 200) {
      // Se queda en esta pantalla: aviso con toast y se vuelven a leer los datos guardados.
      toast.success('Información adicional actualizada.');
      const { status: reloadStatus, data } = await callEndpoint(
        getPropertyToEditEndpoint(id, token),
      );
      if (reloadStatus === 200) {
        setFormUpdateData(fetchResponseAdapter(data).data);
      }
    } else {
      toast.error('No se pudo actualizar la propiedad.');
    }
  };

  // Sin esto, un campo inválido que no se ve en pantalla hace que el botón parezca roto.
  const onInvalid = (formErrors: FieldErrors<PropertyAdditionalInfoForm>) => {
    console.error('[additional-info] formulario inválido', formErrors);
    const messages = Object.values(formErrors)
      .map((error) => error?.message)
      .filter(Boolean);
    toast.error(
      messages.length
        ? `Revisa el formulario: ${messages.join('. ')}`
        : 'Revisa los campos del formulario.',
    );
  };

  const safePropertyTypes = Array.isArray(catalogs.propertyTypes)
    ? catalogs.propertyTypes.filter(x => !!x && typeof x.id !== 'undefined' && x.id !== null)
    : [];

  let selectedType: typeof safePropertyTypes[0] | undefined = undefined;
  if (formUpdateData && formUpdateData.type && formUpdateData.type.id) {
    selectedType = safePropertyTypes.find(x => x.id === formUpdateData.type.id);
  }

  return {
    isLoading,
    control,
    register,
    setValue,
    watch,
    landTypeIds,
    errors,
    handleSubmit,
    formUpdateData,
    doAction,
    onInvalid,
    catalogs,
    loadingCurrency,
    currentCurrency,
    propersOptions,
    isLoadingPropers
  };
};

export default useAdminAdditionalInfo;
